import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { asDecimalString, Money, type DecimalString } from "@shop-ledger/shared-types";
import { Button, Input } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listInventory } from "../../api/inventoryApi";
import { listProductImages } from "../../api/imagesApi";
import { getBranding } from "../../api/brandingApi";
import { createCustomer, listCustomers } from "../../api/customersApi";
import {
  createGeneratedDocument,
  deleteGeneratedDocument,
  listGeneratedDocuments,
  updateGeneratedDocument,
} from "../../api/documentsApi";
import type {
  CustomerOut,
  GeneratedDocumentCreate,
  GeneratedDocumentOut,
  InventoryItemOutExt,
} from "../../api/types";
import { API_ROOT_URL } from "../../env";
import { StatusBanner } from "../../components/StatusBanner";
import { CustomFieldsEditor } from "../../components/CustomFieldsEditor";
import { useAsyncData } from "../../lib/useAsyncData";
import { ApiError } from "../../lib/apiClient";
import { SaveDocumentModal } from "./SaveDocumentModal";

const TENANT_CURRENCY_FALLBACK = "UGX"; // same documented stand-in as the POS till

type DocType = "QUOTATION" | "INVOICE" | "RECEIPT";
type TemplateKey = "classic" | "modern" | "minimal" | "bold" | "gradient";
type StatusFilter = "ALL" | "DRAFT" | "FINAL";

const DOC_TYPE_LABELS: Record<DocType, string> = {
  QUOTATION: "Quotation",
  INVOICE: "Invoice",
  RECEIPT: "Receipt",
};
const DOC_NUMBER_PREFIX: Record<DocType, string> = {
  QUOTATION: "QUO",
  INVOICE: "INV",
  RECEIPT: "RCT",
};
/** The industry-standard skeleton is the same for all four (research pass: logo + seller
 * block, client "Bill To" block, doc number/date block, itemized table, prominent totals,
 * terms footer) — the templates vary typography/color/weight, never which fields exist. */
const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  classic: "Classic",
  modern: "Modern",
  minimal: "Minimal",
  bold: "Bold",
  gradient: "Gradient",
};

interface DocLine {
  key: string; // local list key, not an entity id
  inventoryItemId: string | null; // null = manually added line (e.g. labour)
  name: string;
  detail: string; // secondary description under the name (SKU, or free text)
  quantity: string;
  unitPrice: string;
  imageUrl: string | null;
}

interface ClientDetails {
  name: string;
  phone: string;
  location: string;
  email: string;
  tin: string;
  customFields: Record<string, string>;
}

const BLANK_CLIENT: ClientDetails = { name: "", phone: "", location: "", email: "", tin: "", customFields: {} };

function counterStorageKey(tenantId: string, docType: DocType): string {
  return `sl-doc-counter:${tenantId}:${docType}`;
}
function autosaveStorageKey(tenantId: string): string {
  return `sl-doc-autosave:${tenantId}`;
}

/** In-progress editor state (business/issuer details excluded — those now come live from
 * System → Branding, not per-device storage). Restores "refreshing the page shouldn't lose
 * your inputs" without needing a backend save. */
interface AutosavedState {
  docType: DocType;
  template: TemplateKey;
  showImages: boolean;
  showLogo: boolean;
  showWatermark: boolean;
  docNumber: string;
  docNumberEdited: boolean;
  issueDate: string;
  secondaryDate: string;
  client: ClientDetails;
  lines: DocLine[];
  discount: string;
  taxPercent: string;
  notes: string;
}

function loadAutosave(tenantId: string): Partial<AutosavedState> | null {
  try {
    const raw = localStorage.getItem(autosaveStorageKey(tenantId));
    return raw ? (JSON.parse(raw) as Partial<AutosavedState>) : null;
  } catch {
    return null;
  }
}

/** Next suggested number, e.g. `INV-2026-0007` — a per-device convenience counter,
 * always editable before print/save. */
function suggestDocNumber(tenantId: string, docType: DocType): string {
  let n = 1;
  try {
    n = Number(localStorage.getItem(counterStorageKey(tenantId, docType)) ?? "0") + 1;
    if (!Number.isFinite(n) || n < 1) n = 1;
  } catch {
    n = 1;
  }
  return `${DOC_NUMBER_PREFIX[docType]}-${new Date().getFullYear()}-${String(n).padStart(4, "0")}`;
}

function bumpDocCounter(tenantId: string, docType: DocType): void {
  try {
    const current = Number(localStorage.getItem(counterStorageKey(tenantId, docType)) ?? "0");
    localStorage.setItem(counterStorageKey(tenantId, docType), String((Number.isFinite(current) ? current : 0) + 1));
  } catch {
    // storage unavailable — numbering just won't auto-advance
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLong(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/** Quantity × unit price for one line, tolerant of half-typed input ("", "."). */
function lineTotal(line: DocLine): DecimalString {
  try {
    return Money.multiply(line.quantity || "0", line.unitPrice || "0");
  } catch {
    return Money.zero();
  }
}

let lineKeyCounter = 0;
function nextLineKey(): string {
  lineKeyCounter += 1;
  return `line-${lineKeyCounter}`;
}

const STATUS_FILTERS: StatusFilter[] = ["ALL", "DRAFT", "FINAL"];

/**
 * One-page quotation/invoice/receipt generator (business-owner request), living in the
 * simple-view nav: pick products (or add manual lines like labour), adjust prices and
 * quantities, toggle product pictures, fill in client details, choose one of four
 * industry-standard A4 templates, and print / save as PDF via the browser print dialog —
 * the same zero-dependency technique the POS receipt preview and barcode labels use.
 * Business details (address/phone/TIN/custom fields) come live from System → Branding
 * (set once, reused everywhere) rather than being retyped per document. A document can
 * also be saved (draft or final) to the backend for later reload/reprint via the "Saved
 * documents" panel — it still never posts to the ledger: a real ledger-posting invoice
 * belongs to Invoices & Bills.
 */
export function DocumentGeneratorPage() {
  const { user, ensureFreshToken } = useAuth();
  const tenantId = user?.tenant_id ?? "unknown";

  const [docType, setDocType] = useState<DocType>("INVOICE");
  const [template, setTemplate] = useState<TemplateKey>("classic");
  // Pictures on by default (business-owner follow-up) — a product without images simply
  // renders without one, so the default costs nothing on an image-less catalog.
  const [showImages, setShowImages] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  // Diagonal business-name watermark for document authenticity (business-owner
  // follow-up) — on by default, toggleable per document.
  const [showWatermark, setShowWatermark] = useState(true);
  // Phone-width layout (business-owner follow-up: the side-by-side builder+preview
  // "looks awful on mobile"): two tabs — Edit and Preview — each showing one pane
  // full-width. Pure CSS on desktop (both panes visible, tab strip hidden). Separate
  // from `showSaved` below, which replaces this whole layout at every viewport.
  const [mobilePane, setMobilePane] = useState<"edit" | "preview">("edit");
  const [showSaved, setShowSaved] = useState(false);

  const [docNumber, setDocNumber] = useState(() => suggestDocNumber(tenantId, "INVOICE"));
  const [docNumberEdited, setDocNumberEdited] = useState(false);
  const [issueDate, setIssueDate] = useState(todayIso);
  const [secondaryDate, setSecondaryDate] = useState(""); // due date / valid-until; blank = omitted

  const [client, setClient] = useState<ClientDetails>(BLANK_CLIENT);
  // Set when the current client came from picking an existing customer (search result or a
  // loaded document) — clears the moment the client is hand-edited again, so "Save this
  // client for future documents" only offers to create a *new* customer record.
  const [appliedCustomerId, setAppliedCustomerId] = useState<string | null>(null);
  const [saveClientAsCustomer, setSaveClientAsCustomer] = useState(true);
  const [lines, setLines] = useState<DocLine[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [taxPercent, setTaxPercent] = useState("");
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  const [printedNote, setPrintedNote] = useState<string | null>(null);

  // Backend identity of the document currently loaded in the editor, if any — null means
  // "not yet saved" (next Save creates); non-null means a later Save updates this same row.
  const [loadedDocumentId, setLoadedDocumentId] = useState<string | null>(null);
  // Remembered so re-opening Save on an already-loaded document pre-fills its real name/status
  // instead of a freshly-guessed default — otherwise every re-save prompted for a fresh name.
  const [loadedDocumentName, setLoadedDocumentName] = useState<string | null>(null);
  const [loadedDocumentStatus, setLoadedDocumentStatus] = useState<"DRAFT" | "FINAL">("DRAFT");
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [pendingReprint, setPendingReprint] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [savedDateFrom, setSavedDateFrom] = useState("");
  const [savedDateTo, setSavedDateTo] = useState("");
  const [savedSearch, setSavedSearch] = useState("");

  const didRestoreAutosave = useRef(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Doc number follows the type until the user edits it by hand.
  useEffect(() => {
    if (!docNumberEdited) setDocNumber(suggestDocNumber(tenantId, docType));
  }, [docType, tenantId, docNumberEdited]);

  // Restore in-progress inputs once on mount ("refreshing the page shouldn't lose your
  // inputs") — only ever runs once, so a later applyLoadedDocument() call isn't fought.
  useEffect(() => {
    if (didRestoreAutosave.current) return;
    didRestoreAutosave.current = true;
    const saved = loadAutosave(tenantId);
    if (!saved) return;
    if (saved.docType) setDocType(saved.docType);
    if (saved.template) setTemplate(saved.template);
    if (typeof saved.showImages === "boolean") setShowImages(saved.showImages);
    if (typeof saved.showLogo === "boolean") setShowLogo(saved.showLogo);
    if (typeof saved.showWatermark === "boolean") setShowWatermark(saved.showWatermark);
    if (saved.docNumber) setDocNumber(saved.docNumber);
    if (typeof saved.docNumberEdited === "boolean") setDocNumberEdited(saved.docNumberEdited);
    if (saved.issueDate) setIssueDate(saved.issueDate);
    if (typeof saved.secondaryDate === "string") setSecondaryDate(saved.secondaryDate);
    if (saved.client) setClient({ ...BLANK_CLIENT, ...saved.client });
    if (saved.lines) setLines(saved.lines);
    if (typeof saved.discount === "string") setDiscount(saved.discount);
    if (typeof saved.taxPercent === "string") setTaxPercent(saved.taxPercent);
    if (typeof saved.notes === "string") setNotes(saved.notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Debounced autosave of the whole in-progress document (not the branding-sourced issuer
  // details, which aren't page state at all now) — cleared on a successful backend Save,
  // left alone on Print (a print-then-accidental-refresh shouldn't also lose unsaved input).
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const snapshot: AutosavedState = {
        docType,
        template,
        showImages,
        showLogo,
        showWatermark,
        docNumber,
        docNumberEdited,
        issueDate,
        secondaryDate,
        client,
        lines,
        discount,
        taxPercent,
        notes,
      };
      try {
        localStorage.setItem(autosaveStorageKey(tenantId), JSON.stringify(snapshot));
      } catch {
        // best-effort only
      }
    }, 400);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [tenantId, docType, template, showImages, showLogo, showWatermark, docNumber, docNumberEdited, issueDate, secondaryDate, client, lines, discount, taxPercent, notes]);

  const brandingState = useAsyncData(() => ensureFreshToken().then((token) => getBranding(token)), []);
  const inventoryState = useAsyncData(() => ensureFreshToken().then((token) => listInventory(token)), []);
  const customersState = useAsyncData(() => ensureFreshToken().then((token) => listCustomers(token)), []);
  // Always fetched unfiltered — status/date/search are applied client-side below so the
  // summary metrics can always reflect the true total regardless of which filter is active,
  // and switching filters never costs a network round-trip.
  const documentsState = useAsyncData(() => ensureFreshToken().then((token) => listGeneratedDocuments(token)), []);

  const savedSummary = useMemo(() => {
    const all = documentsState.data ?? [];
    return {
      total: all.length,
      draft: all.filter((d) => d.status === "DRAFT").length,
      final: all.filter((d) => d.status === "FINAL").length,
      quotation: all.filter((d) => d.doc_type === "QUOTATION").length,
      invoice: all.filter((d) => d.doc_type === "INVOICE").length,
      receipt: all.filter((d) => d.doc_type === "RECEIPT").length,
    };
  }, [documentsState.data]);

  const filteredSavedDocuments = useMemo(() => {
    const q = savedSearch.trim().toLowerCase();
    return (documentsState.data ?? []).filter((doc) => {
      if (statusFilter !== "ALL" && doc.status !== statusFilter) return false;
      if (savedDateFrom && doc.issue_date < savedDateFrom) return false;
      if (savedDateTo && doc.issue_date > savedDateTo) return false;
      if (q && !doc.name.toLowerCase().includes(q) && !doc.doc_number.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documentsState.data, statusFilter, savedDateFrom, savedDateTo, savedSearch]);

  const branding = brandingState.data;
  const businessName = branding?.display_name?.trim() || "ZEL-MART";
  const logoUrl = branding?.logo_path
    ? branding.logo_path.startsWith("http")
      ? branding.logo_path
      : `${API_ROOT_URL}${branding.logo_path}`
    : null;
  const issuerCustomFieldEntries = Object.entries(branding?.custom_fields ?? {});

  const filteredProducts = useMemo(() => {
    const all = inventoryState.data ?? [];
    const q = productSearch.trim().toLowerCase();
    if (!q) return [];
    return all.filter((item) => item.name.toLowerCase().includes(q) || item.sku.toLowerCase().includes(q)).slice(0, 8);
  }, [inventoryState.data, productSearch]);

  const filteredCustomers = useMemo(() => {
    const all = customersState.data ?? [];
    const q = customerSearch.trim().toLowerCase();
    if (!q) return [];
    return all.filter((c) => c.name.toLowerCase().includes(q) || (c.phone ?? "").includes(q)).slice(0, 6);
  }, [customersState.data, customerSearch]);

  function addProductLine(item: InventoryItemOutExt) {
    const key = nextLineKey();
    setLines((prev) => [
      ...prev,
      {
        key,
        inventoryItemId: item.id,
        name: item.name,
        detail: item.sku,
        quantity: "1",
        unitPrice: item.retail_price,
        imageUrl: null,
      },
    ]);
    setProductSearch("");
    // Primary image fetched lazily, best-effort — only matters when "show pictures" is
    // on, and a product without images simply renders without one.
    void ensureFreshToken()
      .then((token) => listProductImages(token, item.id))
      .then((images) => {
        const primary = images.find((img) => img.is_primary) ?? images[0];
        if (!primary) return;
        setLines((prev) =>
          prev.map((line) => (line.key === key ? { ...line, imageUrl: `${API_ROOT_URL}${primary.thumb_path}` } : line)),
        );
      })
      .catch(() => undefined);
  }

  function addManualLine() {
    setLines((prev) => [
      ...prev,
      { key: nextLineKey(), inventoryItemId: null, name: "", detail: "", quantity: "1", unitPrice: "0", imageUrl: null },
    ]);
  }

  function updateLine(key: string, patch: Partial<DocLine>) {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((line) => line.key !== key));
  }

  function updateClient(patch: Partial<ClientDetails>) {
    setClient((prev) => ({ ...prev, ...patch }));
    setAppliedCustomerId(null); // hand-edited — no longer exactly the applied customer record
  }

  function applyCustomer(customer: CustomerOut) {
    setClient({
      name: customer.name,
      phone: customer.phone ?? "",
      location: customer.location ?? "",
      email: customer.email ?? "",
      tin: customer.tin ?? "",
      customFields: customer.custom_fields ?? {},
    });
    setAppliedCustomerId(customer.id);
    setCustomerSearch("");
  }

  const subtotal = useMemo(() => Money.sum(lines.map(lineTotal)), [lines]);
  const discountAmount = useMemo(() => {
    try {
      return discount.trim() ? Money.round(discount) : Money.zero();
    } catch {
      return Money.zero();
    }
  }, [discount]);
  const taxAmount = useMemo(() => {
    try {
      const pct = taxPercent.trim();
      if (!pct) return Money.zero();
      const base = Money.subtract(subtotal, discountAmount);
      return asDecimalString(Money.of(base).times(Money.of(pct)).dividedBy(100).toFixed(2));
    } catch {
      return Money.zero();
    }
  }, [taxPercent, subtotal, discountAmount]);
  const grandTotal = useMemo(
    () => Money.add(Money.subtract(subtotal, discountAmount), taxAmount),
    [subtotal, discountAmount, taxAmount],
  );

  const currency = TENANT_CURRENCY_FALLBACK;
  const fmt = (value: DecimalString | string) => Money.format(value, currency);

  const secondaryDateLabel = docType === "QUOTATION" ? "Valid until" : docType === "INVOICE" ? "Due date" : "";
  const anyImages = showImages && lines.some((line) => line.imageUrl);

  function handlePrint() {
    bumpDocCounter(tenantId, docType);
    setPrintedNote(`${DOC_TYPE_LABELS[docType]} ${docNumber} sent to the print dialog — choose "Save as PDF" there for a file.`);
    // Blanking the tab title removes it from the browser's own print header (the
    // "ZEL-MART — Admin" text at the top of the printed page) — the date/URL/page
    // number in that same browser-native header/footer can only be removed by the user
    // via the print dialog's own "Headers and footers" toggle (see the on-page tip),
    // no page can suppress those from script. Idempotent restore, since both the
    // `afterprint` listener and the timeout fallback can each fire (Safari doesn't always
    // dispatch `afterprint` for a script-triggered window.print()).
    const originalTitle = document.title;
    document.title = "";
    let restored = false;
    const restoreTitle = () => {
      if (restored) return;
      restored = true;
      document.title = originalTitle;
    };
    window.addEventListener("afterprint", restoreTitle, { once: true });
    setTimeout(restoreTitle, 1000);
    // Next document of this type gets a fresh suggested number (unless hand-edited).
    window.print();
    if (!docNumberEdited) setDocNumber(suggestDocNumber(tenantId, docType));
  }

  // Fires after applyLoadedDocument's populate render has actually committed, so print
  // reads the newly loaded DOM rather than whatever was on screen a moment ago.
  useEffect(() => {
    if (!pendingReprint) return;
    handlePrint();
    setPendingReprint(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReprint]);

  function applyLoadedDocument(doc: GeneratedDocumentOut) {
    setDocType(doc.doc_type);
    setTemplate(doc.template);
    setDocNumber(doc.doc_number);
    setDocNumberEdited(true); // a loaded/reprinted number shouldn't auto-advance under it
    setIssueDate(doc.issue_date);
    setSecondaryDate(doc.secondary_date ?? "");
    setClient({
      name: doc.client.name,
      phone: doc.client.phone ?? "",
      location: doc.client.location ?? "",
      email: doc.client.email ?? "",
      tin: doc.client.tin ?? "",
      customFields: doc.client.custom_fields ?? {},
    });
    setAppliedCustomerId(null);
    setLines(
      doc.lines.map((line) => ({
        key: nextLineKey(),
        inventoryItemId: line.inventory_item_id,
        name: line.name,
        detail: line.detail ?? "",
        quantity: line.quantity,
        unitPrice: line.unit_price,
        imageUrl: line.image_url,
      })),
    );
    setDiscount(doc.discount ?? "");
    setTaxPercent(doc.tax_percent ?? "");
    setNotes(doc.notes ?? "");
    setShowImages(doc.show_images);
    setShowLogo(doc.show_logo);
    setShowWatermark(doc.show_watermark);
  }

  function handleLoadDraft(doc: GeneratedDocumentOut) {
    applyLoadedDocument(doc);
    setLoadedDocumentId(doc.id);
    setLoadedDocumentName(doc.name);
    setLoadedDocumentStatus(doc.status === "FINAL" ? "FINAL" : "DRAFT");
    setShowSaved(false);
    setMobilePane("edit");
  }

  function handleReprint(doc: GeneratedDocumentOut) {
    applyLoadedDocument(doc);
    setLoadedDocumentId(null); // editing after a reprint creates a new record, not an overwrite
    // Keep the name as a starting point for a "Save As" of the reprinted document — Save
    // creates a new record either way since loadedDocumentId is null above.
    setLoadedDocumentName(doc.name);
    setLoadedDocumentStatus("DRAFT");
    setShowSaved(false);
    setMobilePane("preview");
    setPendingReprint(true);
  }

  async function handleDeleteSaved(doc: GeneratedDocumentOut) {
    if (!window.confirm(`Delete "${doc.name}"? This can't be undone.`)) return;
    try {
      const token = await ensureFreshToken();
      await deleteGeneratedDocument(token, doc.id);
      documentsState.reload();
    } catch {
      // best-effort — list stays as-is, user can retry
    }
  }

  function handleNewDocument() {
    setLoadedDocumentId(null);
    setLoadedDocumentName(null);
    setLoadedDocumentStatus("DRAFT");
    setDocType("INVOICE");
    setTemplate("classic");
    setShowImages(true);
    setShowLogo(true);
    setShowWatermark(true);
    setClient(BLANK_CLIENT);
    setAppliedCustomerId(null);
    setLines([]);
    setDiscount("");
    setTaxPercent("");
    setNotes("");
    setSecondaryDate("");
    setIssueDate(todayIso());
    setDocNumberEdited(false);
    setDocNumber(suggestDocNumber(tenantId, "INVOICE"));
    try {
      localStorage.removeItem(autosaveStorageKey(tenantId));
    } catch {
      // best-effort only
    }
  }

  async function handleSaveDocument(name: string, status: "DRAFT" | "FINAL") {
    setSaving(true);
    setSaveError(null);
    try {
      const token = await ensureFreshToken();
      const payload: GeneratedDocumentCreate = {
        name,
        status,
        doc_type: docType,
        template,
        branch_id: null,
        doc_number: docNumber,
        issue_date: issueDate,
        secondary_date: secondaryDate || null,
        issuer: {
          address: branding?.address ?? null,
          phone: branding?.phone ?? null,
          email: null,
          tin: branding?.tin ?? null,
          custom_fields: branding?.custom_fields ?? {},
        },
        client: {
          name: client.name,
          phone: client.phone || null,
          location: client.location || null,
          email: client.email || null,
          tin: client.tin || null,
          custom_fields: client.customFields,
        },
        lines: lines.map((line) => ({
          inventory_item_id: line.inventoryItemId,
          name: line.name,
          detail: line.detail || null,
          quantity: asDecimalString(line.quantity || "0"),
          unit_price: asDecimalString(line.unitPrice || "0"),
          image_url: line.imageUrl,
        })),
        discount: discount ? asDecimalString(discount) : null,
        tax_percent: taxPercent ? asDecimalString(taxPercent) : null,
        notes: notes || null,
        show_images: showImages,
        show_logo: showLogo,
        show_watermark: showWatermark,
        currency,
      };

      const result = loadedDocumentId
        ? await updateGeneratedDocument(token, loadedDocumentId, payload)
        : await createGeneratedDocument(token, payload);
      setLoadedDocumentId(result.id);
      setLoadedDocumentName(result.name);
      setLoadedDocumentStatus(result.status === "FINAL" ? "FINAL" : "DRAFT");

      // Best-effort — a failed customer save shouldn't block the document save itself.
      if (saveClientAsCustomer && appliedCustomerId === null && client.name.trim()) {
        try {
          await createCustomer(token, {
            name: client.name,
            phone: client.phone || undefined,
            email: client.email || undefined,
            location: client.location || undefined,
            tin: client.tin || undefined,
            custom_fields: client.customFields,
          });
          customersState.reload();
        } catch {
          // ignore — document save already succeeded
        }
      }

      try {
        localStorage.removeItem(autosaveStorageKey(tenantId));
      } catch {
        // best-effort only
      }
      setSaveNote(`"${name}" saved as ${status === "FINAL" ? "a final document" : "a draft"}.`);
      setSaveModalOpen(false);
      documentsState.reload();
    } catch (error) {
      setSaveError(error instanceof ApiError || error instanceof Error ? error.message : "Could not save document.");
    } finally {
      setSaving(false);
    }
  }

  const showSaveClientCheckbox = client.name.trim().length > 0 && appliedCustomerId === null;

  return (
    <div className="page sl-docgen">
      {/* Scoped A4 page setup — mounted with this page only, so the app-wide print CSS
          for barcode labels and POS receipts never inherits an A4 page size. */}
      <style>{"@page { size: A4; margin: 12mm; }"}</style>
      <div className="sl-docgen__page-header">
        <div>
          <h1 className="page__title">Documents</h1>
          <p className="muted">
            Build a quotation, invoice, or receipt and print it (or save it as a PDF) on A4 paper. Documents made here are
            for handing to a client — they are not posted to the ledger (use Invoices &amp; Bills for that).
          </p>
        </div>
        <div className="sl-docgen__page-header-actions">
          <Button type="button" variant="secondary" onClick={handleNewDocument}>
            New document
          </Button>
          <Button type="button" variant="secondary" onClick={() => setShowSaved((prev) => !prev)}>
            {showSaved ? "Back to editor" : "Saved documents"}
          </Button>
        </div>
      </div>
      {printedNote ? <StatusBanner kind="info">{printedNote}</StatusBanner> : null}
      {saveNote ? <StatusBanner kind="info">{saveNote}</StatusBanner> : null}

      {showSaved ? (
        <section className="panel sl-docgen__saved">
          <h2 className="panel__title">Saved documents</h2>

          <div className="sl-docgen__stats">
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.total}</div>
              <div className="sl-docgen__stat-label">Total</div>
            </div>
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.draft}</div>
              <div className="sl-docgen__stat-label">Drafts</div>
            </div>
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.final}</div>
              <div className="sl-docgen__stat-label">Final</div>
            </div>
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.quotation}</div>
              <div className="sl-docgen__stat-label">Quotations</div>
            </div>
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.invoice}</div>
              <div className="sl-docgen__stat-label">Invoices</div>
            </div>
            <div className="sl-docgen__stat">
              <div className="sl-docgen__stat-value">{savedSummary.receipt}</div>
              <div className="sl-docgen__stat-label">Receipts</div>
            </div>
          </div>

          <div className="sl-docgen__field-grid">
            <Input label="From" type="date" value={savedDateFrom} onChange={(event) => setSavedDateFrom(event.target.value)} />
            <Input label="To" type="date" value={savedDateTo} onChange={(event) => setSavedDateTo(event.target.value)} />
            <Input
              label="Search"
              value={savedSearch}
              onChange={(event) => setSavedSearch(event.target.value)}
              placeholder="Name or document number"
            />
          </div>

          <div className="sl-docgen__choice-row" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`sl-docgen__choice ${statusFilter === key ? "sl-docgen__choice--active" : ""}`}
                onClick={() => setStatusFilter(key)}
              >
                {key === "ALL" ? "All" : key === "DRAFT" ? "Drafts" : "Final"}
              </button>
            ))}
          </div>
          {documentsState.error ? <StatusBanner kind="error">{documentsState.error}</StatusBanner> : null}
          {documentsState.loading ? <p className="muted">Loading…</p> : null}
          {!documentsState.loading && (documentsState.data ?? []).length === 0 ? (
            <p className="muted">No saved documents yet.</p>
          ) : !documentsState.loading && filteredSavedDocuments.length === 0 ? (
            <p className="muted">No documents match these filters.</p>
          ) : (
            <ul className="sl-docgen__lines">
              {filteredSavedDocuments.map((doc) => (
                <li key={doc.id} className="sl-docgen__line">
                  <div className="sl-docgen__line-fields">
                    <div>
                      <div className="sl-doc-sheet__item-name">{doc.name}</div>
                      <div className="sl-doc-sheet__item-detail">
                        {DOC_TYPE_LABELS[doc.doc_type]} · {doc.doc_number} · {formatDateLong(doc.issue_date)}
                      </div>
                    </div>
                    <span className={`badge ${doc.status === "FINAL" ? "badge--current" : "badge--due-soon"}`}>
                      {doc.status === "FINAL" ? "Final" : "Draft"}
                    </span>
                  </div>
                  <div className="sl-docgen__line-side">
                    {doc.status === "DRAFT" ? (
                      <Button type="button" variant="secondary" size="md" onClick={() => handleLoadDraft(doc)}>
                        Load &amp; edit
                      </Button>
                    ) : (
                      <Button type="button" variant="secondary" size="md" onClick={() => handleReprint(doc)}>
                        Reprint
                      </Button>
                    )}
                    <button type="button" className="sl-docgen__line-remove" onClick={() => void handleDeleteSaved(doc)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          <div className="sl-docgen__pane-tabs" role="tablist" aria-label="Builder view">
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === "edit"}
              className={`sl-docgen__pane-tab ${mobilePane === "edit" ? "sl-docgen__pane-tab--active" : ""}`}
              onClick={() => setMobilePane("edit")}
            >
              Edit details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === "preview"}
              className={`sl-docgen__pane-tab ${mobilePane === "preview" ? "sl-docgen__pane-tab--active" : ""}`}
              onClick={() => setMobilePane("preview")}
            >
              Preview
            </button>
          </div>

          <div className={`sl-docgen__layout sl-docgen__layout--${mobilePane}`}>
            {/* ------------------------- controls column ------------------------- */}
            <div className="sl-docgen__controls">
              <section className="panel">
                <h2 className="panel__title">Document</h2>
                <div className="sl-docgen__choice-row" role="group" aria-label="Document type">
                  {(Object.keys(DOC_TYPE_LABELS) as DocType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`sl-docgen__choice ${docType === type ? "sl-docgen__choice--active" : ""}`}
                      onClick={() => setDocType(type)}
                    >
                      {DOC_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>
                <div className="sl-docgen__choice-row" role="group" aria-label="Template">
                  {(Object.keys(TEMPLATE_LABELS) as TemplateKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`sl-docgen__choice sl-docgen__choice--template sl-docgen__choice--tpl-${key} ${
                        template === key ? "sl-docgen__choice--active" : ""
                      }`}
                      onClick={() => setTemplate(key)}
                    >
                      {TEMPLATE_LABELS[key]}
                    </button>
                  ))}
                </div>
                <div className="sl-docgen__field-grid">
                  <Input
                    label={`${DOC_TYPE_LABELS[docType]} number`}
                    value={docNumber}
                    onChange={(event) => {
                      setDocNumber(event.target.value);
                      setDocNumberEdited(true);
                    }}
                  />
                  <Input label="Issue date" type="date" value={issueDate} onChange={(event) => setIssueDate(event.target.value)} />
                  {secondaryDateLabel ? (
                    <Input
                      label={`${secondaryDateLabel} (optional)`}
                      type="date"
                      value={secondaryDate}
                      onChange={(event) => setSecondaryDate(event.target.value)}
                    />
                  ) : null}
                </div>
                <label className="sl-docgen__toggle">
                  <input type="checkbox" checked={showImages} onChange={(event) => setShowImages(event.target.checked)} />
                  Show product pictures
                </label>
                <label className="sl-docgen__toggle">
                  <input type="checkbox" checked={showLogo} onChange={(event) => setShowLogo(event.target.checked)} />
                  Show business logo
                </label>
                <label className="sl-docgen__toggle">
                  <input
                    type="checkbox"
                    checked={showWatermark}
                    onChange={(event) => setShowWatermark(event.target.checked)}
                  />
                  Show watermark
                </label>
              </section>

              <section className="panel">
                <h2 className="panel__title">Your business</h2>
                <p className="muted">
                  Set once in <Link to="/system?tab=branding">System → Branding</Link> and reused on every document.
                </p>
                <div className="sl-docgen__readonly-lines">
                  <div className="sl-doc-sheet__business">{businessName}</div>
                  {branding?.address ? <div className="sl-doc-sheet__contact">{branding.address}</div> : null}
                  {branding?.phone ? <div className="sl-doc-sheet__contact">{branding.phone}</div> : null}
                  {branding?.tin ? <div className="sl-doc-sheet__contact">TIN: {branding.tin}</div> : null}
                  {issuerCustomFieldEntries.map(([key, value]) => (
                    <div key={key} className="sl-doc-sheet__contact">
                      {key}: {value}
                    </div>
                  ))}
                </div>
              </section>

              <section className="panel">
                <h2 className="panel__title">Client</h2>
                <div className="sl-docgen__search">
                  <Input
                    label="Find existing customer (optional)"
                    value={customerSearch}
                    onChange={(event) => setCustomerSearch(event.target.value)}
                    placeholder="Search by name or phone"
                  />
                  {filteredCustomers.length > 0 ? (
                    <ul className="sl-docgen__search-results">
                      {filteredCustomers.map((customer) => (
                        <li key={customer.id}>
                          <button type="button" onClick={() => applyCustomer(customer)}>
                            {customer.name}
                            {customer.phone ? <span className="muted"> · {customer.phone}</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div className="sl-docgen__field-grid">
                  <Input label="Client name" value={client.name} onChange={(event) => updateClient({ name: event.target.value })} />
                  <Input label="Phone" value={client.phone} onChange={(event) => updateClient({ phone: event.target.value })} />
                  <Input label="Location" value={client.location} onChange={(event) => updateClient({ location: event.target.value })} />
                  <Input label="Email" value={client.email} onChange={(event) => updateClient({ email: event.target.value })} />
                  <Input
                    label="Tax Identification Number (TIN)"
                    value={client.tin}
                    onChange={(event) => updateClient({ tin: event.target.value })}
                  />
                </div>
                <CustomFieldsEditor value={client.customFields} onChange={(next) => updateClient({ customFields: next })} />
                {showSaveClientCheckbox ? (
                  <label className="sl-docgen__toggle">
                    <input
                      type="checkbox"
                      checked={saveClientAsCustomer}
                      onChange={(event) => setSaveClientAsCustomer(event.target.checked)}
                    />
                    Save this client for future documents
                  </label>
                ) : null}
              </section>

              <section className="panel">
                <h2 className="panel__title">Items</h2>
                {inventoryState.error ? <StatusBanner kind="error">{inventoryState.error}</StatusBanner> : null}
                <div className="sl-docgen__search">
                  <Input
                    label="Add a product"
                    value={productSearch}
                    onChange={(event) => setProductSearch(event.target.value)}
                    placeholder="Search by name or SKU"
                  />
                  {filteredProducts.length > 0 ? (
                    <ul className="sl-docgen__search-results">
                      {filteredProducts.map((item) => (
                        <li key={item.id}>
                          <button type="button" onClick={() => addProductLine(item)}>
                            {item.name} <span className="muted">· {item.sku} · {fmt(item.retail_price)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <Button type="button" variant="secondary" onClick={addManualLine}>
                  + Add manual line (e.g. labour)
                </Button>

                {lines.length === 0 ? (
                  <p className="muted">No items yet — search a product above or add a manual line.</p>
                ) : (
                  <ul className="sl-docgen__lines">
                    {lines.map((line) => (
                      <li key={line.key} className="sl-docgen__line">
                        <div className="sl-docgen__line-fields">
                          <Input
                            label="Item"
                            value={line.name}
                            onChange={(event) => updateLine(line.key, { name: event.target.value })}
                            placeholder={line.inventoryItemId ? undefined : "e.g. Labour — installation"}
                          />
                          <Input
                            label="Detail (optional)"
                            value={line.detail}
                            onChange={(event) => updateLine(line.key, { detail: event.target.value })}
                          />
                          <Input
                            label="Qty"
                            inputMode="decimal"
                            value={line.quantity}
                            onChange={(event) => updateLine(line.key, { quantity: event.target.value.replace(/[^0-9.]/g, "") })}
                          />
                          <Input
                            label={`Unit price (${currency})`}
                            inputMode="decimal"
                            value={line.unitPrice}
                            onChange={(event) => updateLine(line.key, { unitPrice: event.target.value.replace(/[^0-9.]/g, "") })}
                          />
                        </div>
                        <div className="sl-docgen__line-side">
                          <span className="sl-docgen__line-total">{fmt(lineTotal(line))}</span>
                          <button type="button" className="sl-docgen__line-remove" onClick={() => removeLine(line.key)}>
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="sl-docgen__field-grid">
                  <Input
                    label="Discount amount (optional)"
                    inputMode="decimal"
                    value={discount}
                    onChange={(event) => setDiscount(event.target.value.replace(/[^0-9.]/g, ""))}
                  />
                  <Input
                    label="Tax % (optional)"
                    inputMode="decimal"
                    value={taxPercent}
                    onChange={(event) => setTaxPercent(event.target.value.replace(/[^0-9.]/g, ""))}
                  />
                </div>
                <label className="sl-field">
                  <span className="sl-field__label">Notes / payment terms (optional)</span>
                  <textarea
                    className="sl-input"
                    rows={3}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="e.g. Payment due within 14 days. Mobile money: 07XX XXX XXX"
                  />
                </label>
              </section>

              <div className="sl-docgen__print-row">
                <Button type="button" variant="secondary" size="lg" onClick={() => setSaveModalOpen(true)}>
                  Save
                </Button>
                <Button type="button" variant="primary" size="lg" disabled={lines.length === 0} onClick={handlePrint}>
                  Print / Save as PDF (A4)
                </Button>
              </div>
              <p className="muted sl-docgen__print-tip">
                For a fully clean PDF, open "More settings" in your browser's print dialog and turn off "Headers and
                footers."
              </p>
            </div>

            {/* ------------------------- live A4 preview ------------------------- */}
            <div className="sl-docgen__preview">
              <div className="sl-docgen__print-row sl-docgen__print-row--preview">
                <Button type="button" variant="secondary" onClick={() => setSaveModalOpen(true)}>
                  Save
                </Button>
                <Button type="button" variant="primary" disabled={lines.length === 0} onClick={handlePrint}>
                  Print / Save as PDF (A4)
                </Button>
              </div>
              <p className="muted sl-docgen__print-tip">
                For a fully clean PDF, open "More settings" in your browser's print dialog and turn off "Headers and
                footers."
              </p>
              <div
                className="sl-doc-sheet"
                data-template={template}
                data-watermark={showWatermark ? businessName : undefined}
              >
                <header className="sl-doc-sheet__head">
                  <div className="sl-doc-sheet__issuer">
                    {showLogo && logoUrl ? <img className="sl-doc-sheet__logo" src={logoUrl} alt={`${businessName} logo`} /> : null}
                    <div>
                      <div className="sl-doc-sheet__business">{businessName}</div>
                      {branding?.address ? <div className="sl-doc-sheet__contact">{branding.address}</div> : null}
                      {branding?.phone ? <div className="sl-doc-sheet__contact">{branding.phone}</div> : null}
                      {branding?.tin ? <div className="sl-doc-sheet__contact">TIN: {branding.tin}</div> : null}
                      {issuerCustomFieldEntries.map(([key, value]) => (
                        <div key={key} className="sl-doc-sheet__contact">
                          {key}: {value}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="sl-doc-sheet__meta">
                    <div className="sl-doc-sheet__doc-type">{DOC_TYPE_LABELS[docType].toUpperCase()}</div>
                    <table className="sl-doc-sheet__meta-table">
                      <tbody>
                        <tr>
                          <td>{DOC_TYPE_LABELS[docType]} No.</td>
                          <td>{docNumber}</td>
                        </tr>
                        <tr>
                          <td>Date</td>
                          <td>{formatDateLong(issueDate)}</td>
                        </tr>
                        {secondaryDate && secondaryDateLabel ? (
                          <tr>
                            <td>{secondaryDateLabel}</td>
                            <td>{formatDateLong(secondaryDate)}</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </header>

                <section className="sl-doc-sheet__parties">
                  <div>
                    <div className="sl-doc-sheet__micro-label">{docType === "QUOTATION" ? "Prepared for" : "Bill to"}</div>
                    <div className="sl-doc-sheet__client-name">{client.name || "—"}</div>
                    {client.location ? <div className="sl-doc-sheet__contact">{client.location}</div> : null}
                    {client.phone ? <div className="sl-doc-sheet__contact">{client.phone}</div> : null}
                    {client.email ? <div className="sl-doc-sheet__contact">{client.email}</div> : null}
                    {client.tin ? <div className="sl-doc-sheet__contact">TIN: {client.tin}</div> : null}
                    {Object.entries(client.customFields).map(([key, value]) => (
                      <div key={key} className="sl-doc-sheet__contact">
                        {key}: {value}
                      </div>
                    ))}
                  </div>
                </section>

                <table className="sl-doc-sheet__table">
                  <thead>
                    <tr>
                      {anyImages ? <th className="sl-doc-sheet__col-img" aria-label="Picture" /> : null}
                      <th>Description</th>
                      <th className="sl-doc-sheet__num sl-doc-sheet__col-qty">Qty</th>
                      <th className="sl-doc-sheet__num sl-doc-sheet__col-unit">Unit price</th>
                      <th className="sl-doc-sheet__num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr>
                        <td colSpan={anyImages ? 5 : 4} className="sl-doc-sheet__empty">
                          Added items will appear here.
                        </td>
                      </tr>
                    ) : (
                      lines.map((line) => (
                        <tr key={line.key}>
                          {anyImages ? (
                            <td className="sl-doc-sheet__col-img">
                              {line.imageUrl ? <img src={line.imageUrl} alt="" className="sl-doc-sheet__thumb" /> : null}
                            </td>
                          ) : null}
                          <td>
                            <div className="sl-doc-sheet__item-name">{line.name || "—"}</div>
                            {line.detail ? <div className="sl-doc-sheet__item-detail">{line.detail}</div> : null}
                            {/* Phone-width stand-in for the Qty/Unit price columns (CSS-hidden
                                there so the Amount column fits a 390px sheet; this line is
                                hidden everywhere else, including print). */}
                            <div className="sl-doc-sheet__item-qty-inline">
                              {line.quantity || "0"} × {fmt(line.unitPrice || "0")}
                            </div>
                          </td>
                          <td className="sl-doc-sheet__num sl-doc-sheet__col-qty">{line.quantity || "0"}</td>
                          <td className="sl-doc-sheet__num sl-doc-sheet__col-unit">{fmt(line.unitPrice || "0")}</td>
                          <td className="sl-doc-sheet__num">{fmt(lineTotal(line))}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <section className="sl-doc-sheet__totals">
                  <table>
                    <tbody>
                      <tr>
                        <td>Subtotal</td>
                        <td>{fmt(subtotal)}</td>
                      </tr>
                      {!Money.isZero(discountAmount) ? (
                        <tr>
                          <td>Discount</td>
                          <td>-{fmt(discountAmount)}</td>
                        </tr>
                      ) : null}
                      {!Money.isZero(taxAmount) ? (
                        <tr>
                          <td>Tax ({taxPercent.trim()}%)</td>
                          <td>{fmt(taxAmount)}</td>
                        </tr>
                      ) : null}
                      <tr className="sl-doc-sheet__grand">
                        <td>{docType === "RECEIPT" ? "Total paid" : "Total due"}</td>
                        <td>{fmt(grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                {notes.trim() ? (
                  <section className="sl-doc-sheet__notes">
                    <div className="sl-doc-sheet__micro-label">Notes / terms</div>
                    <p>{notes}</p>
                  </section>
                ) : null}

                <footer className="sl-doc-sheet__footer">
                  {docType === "RECEIPT"
                    ? `Received with thanks — ${businessName}`
                    : `Thank you for your business — ${businessName}`}
                </footer>
              </div>
            </div>
          </div>
        </>
      )}

      <SaveDocumentModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        initialName={loadedDocumentName ?? `${DOC_TYPE_LABELS[docType]}${client.name ? ` for ${client.name}` : ""}`}
        initialStatus={loadedDocumentStatus}
        saving={saving}
        errorMessage={saveError}
        onSave={(name, status) => void handleSaveDocument(name, status)}
      />
    </div>
  );
}
